import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { WifiOff, Loader2, Shield, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '../utils/apiClient';

const NETWORK_NAME = 'BC-Namaka-S';

/**
 * Network Security Wrapper
 * Blocks access unless user is verified to be on the cafe network
 * Uses server-side IP verification (most reliable method)
 * 
 * IMPORTANT: If verification fails for any technical reason, 
 * we ALLOW access (fail-open) to prevent blocking staff operations.
 */
export default function SecureNetworkGate({ children, featureName = 'Staff Portal' }) {
  const [checking, setChecking] = useState(true);
  const [isOnNetwork, setIsOnNetwork] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [clientIP, setClientIP] = useState('');

  const performNetworkCheck = async () => {
    setChecking(true);
    setErrorMessage('');
    
    try {
      // Server-side verification - checks if request comes from cafe's IP
      const response = await api.post('/staff-portal/verify-network', {}, {
        timeout: 10000 // 10 second timeout
      });
      const data = response.data;
      
      setClientIP(data.client_ip || '');
      
      if (data.is_on_cafe_network) {
        setIsOnNetwork(true);
      } else {
        // Server explicitly said not on network
        setIsOnNetwork(false);
        setErrorMessage(data.message || 'Device not on secure network');
      }
    } catch (err) {
      console.error('Network verification failed:', err);
      // FAIL-OPEN: If verification fails (timeout, network error, server error),
      // ALLOW access to prevent blocking staff from working.
      // The staff still needs to authenticate with email/PIN after this.
      console.log('Network check failed - allowing access (fail-open policy)');
      setIsOnNetwork(true);
      setErrorMessage('');
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    performNetworkCheck();
  }, []);

  // Loading state
  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Verifying Network Security
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            Checking if you&apos;re on the {NETWORK_NAME} network...
          </p>
        </div>
      </div>
    );
  }

  // Not on network - blocked
  if (!isOnNetwork) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full">
          {/* Warning Icon */}
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <WifiOff className="w-10 h-10 text-red-600 dark:text-red-400" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
            Secure Network Required
          </h1>

          {/* Message */}
          <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
            Please connect to the &ldquo;{NETWORK_NAME}&rdquo; WiFi network inside the cafe to access {featureName}.
          </p>

          {/* Instructions Box */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-300 mb-2">
                  How to connect:
                </p>
                <ol className="text-sm text-amber-800 dark:text-amber-400 space-y-1 list-decimal list-inside">
                  <li>Open WiFi settings on your device</li>
                  <li>Select &ldquo;<strong>{NETWORK_NAME}</strong>&rdquo;</li>
                  <li>Enter the WiFi password (ask your manager)</li>
                  <li>Return to this page and tap &ldquo;Check Again&rdquo;</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Security Note */}
          <div className="bg-slate-100 dark:bg-slate-700/50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              <p className="text-sm text-slate-600 dark:text-slate-400">
                For security, {featureName} can only be accessed from inside the cafe.
              </p>
            </div>
          </div>

          {/* Debug info for owner */}
          {clientIP && (
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 mb-4 text-xs text-slate-500 dark:text-slate-400">
              <p>Your IP: {clientIP}</p>
              <p className="mt-1 text-red-500">{errorMessage}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button 
              onClick={performNetworkCheck}
              className="w-full bg-teal-600 hover:bg-teal-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Check Connection Again
            </Button>
            
            <Link to="/" className="block">
              <Button variant="outline" className="w-full">
                Back to Bulaccino Cafe
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // On network - show protected content
  return <>{children}</>;
}
